/**
 * Turns a tapped DOM element into something identifiable weeks later.
 *
 * Direct port of franklycore's src/lib/element-picker.ts (franklycatering
 * #497) — same constants, same six correctness fixes, same algorithm. Pure
 * functions, no DOM mutation — the picking UI lives in RequestButton.tsx.
 */

export interface PickedElement {
  /** CSS-ish path, preferring `[data-testid]` so it is greppable in the repo. */
  selector: string;
  /** Whatever the element actually said on screen, hard-capped. */
  text: string | null;
  /** ARIA role if set, otherwise the tag name. */
  role: string;
  /** Accessible name — aria-label, aria-labelledby, title, alt, or a short own-text. */
  label: string | null;
  /** Nearest `data-testid` on or above the element. The best repo handle there is. */
  testId: string | null;
  /** Page coordinates and size, rounded. */
  rect: { x: number; y: number; width: number; height: number };
  /** Where the tap actually landed, in page coordinates. */
  pointer: { x: number; y: number } | null;
  /**
   * False when the walk could only produce an anonymous container — i.e. we
   * do not really know what he meant. Recorded rather than hidden, because a
   * wrapper `div` presented as a successful pick is worse than an honest miss.
   */
  confident: boolean;
}

/** Tags that are almost always what a person means when they tap. */
const MEANINGFUL_TAGS = new Set(["button", "a", "input", "select", "textarea", "tr", "li"]);

/**
 * ARIA roles that name an actual control. `role` alone is not enough — plenty
 * of layout containers carry `role="none"`, `role="presentation"` or
 * `role="group"`, and stopping the walk on those is how a tap on a tab ends up
 * recorded as the whole composer header.
 */
const MEANINGFUL_ROLES = new Set([
  "button", "link", "tab", "checkbox", "radio", "menuitem", "menuitemcheckbox",
  "menuitemradio", "option", "switch", "textbox", "combobox", "searchbox",
  "slider", "spinbutton", "row", "gridcell", "cell", "listitem", "treeitem",
]);

/**
 * Text longer than this is a container's worth of concatenated labels, not a
 * name.
 */
const MAX_TEXT = 120;
const MAX_LABEL = 80;

/** Four is enough to get from a text node to its control. Six reached the page. */
const MAX_WALK = 4;

/**
 * Radix and friends generate ids like `radix-:r7:` that change every render.
 * An id is only worth putting in a selector if it will still exist tomorrow.
 */
function isStableId(id: string): boolean {
  return id.length > 0 && !id.includes(":") && !/^(radix|headless|aria)-/i.test(id);
}

function describe(node: Element): string {
  const tag = node.tagName.toLowerCase();
  const parent = node.parentElement;
  if (!parent) return tag;

  const siblings = Array.from(parent.children).filter((c) => c.tagName === node.tagName);
  if (siblings.length < 2) return tag;
  return `${tag}:nth-of-type(${siblings.indexOf(node) + 1})`;
}

function isMeaningful(node: Element): boolean {
  if (node.hasAttribute("data-testid")) return true;
  if (MEANINGFUL_TAGS.has(node.tagName.toLowerCase())) return true;
  const role = node.getAttribute("role");
  return !!role && MEANINGFUL_ROLES.has(role);
}

/**
 * A tap on a phone lands wherever it lands. Walk up from the raw hit to the
 * *nearest* thing a person would have named — the button, the row, the link.
 *
 * If nothing on the way up qualifies, return the original hit rather than a
 * wrapper.
 */
export function findMeaningfulTarget(start: Element): Element {
  let node: Element | null = start;
  let depth = 0;

  while (node && node !== document.body && depth <= MAX_WALK) {
    if (isMeaningful(node)) return node;
    node = node.parentElement;
    depth++;
  }
  return start;
}

/**
 * What the browser actually has under the tap point.
 *
 * Chromium does not dispatch `click` on a **disabled** control — the event
 * retargets to an enabled ancestor. Hit-testing the coordinates finds the
 * disabled button that the event never reached (fix #1).
 */
export function targetFromPoint(event: { clientX: number; clientY: number; target: EventTarget | null }): Element | null {
  const hit = document.elementFromPoint(event.clientX, event.clientY);
  if (hit) return hit;
  return event.target instanceof Element ? event.target : null;
}

/**
 * Build a selector for `el`. Stops early at the first `data-testid` or stable
 * id it finds walking up, because those are worth more than a long path.
 */
export function buildSelector(el: Element): string {
  const parts: string[] = [];
  let node: Element | null = el;
  let depth = 0;

  while (node && node !== document.body && depth < 5) {
    const testId = node.getAttribute("data-testid");
    if (testId) {
      parts.unshift(`[data-testid="${testId}"]`);
      return parts.join(" > ");
    }
    if (node.id && isStableId(node.id)) {
      parts.unshift(`#${node.id}`);
      return parts.join(" > ");
    }
    parts.unshift(describe(node));
    node = node.parentElement;
    depth++;
  }
  return parts.join(" > ") || el.tagName.toLowerCase();
}

/** Nearest `data-testid` on or above the element — the strongest repo handle. */
export function findTestId(el: Element): string | null {
  const owner = el.closest("[data-testid]");
  return owner?.getAttribute("data-testid") || null;
}

function clean(value: string | null | undefined): string | null {
  const text = (value || "").replace(/\s+/g, " ").trim();
  return text || null;
}

/**
 * The accessible name, the way a screen reader would announce it. This is
 * what rescues an anonymous container: an icon-only button with
 * `aria-label="Send"` has no text at all, and a container wrapping ten
 * controls has far too much.
 */
export function accessibleName(el: Element): string | null {
  const direct = clean(el.getAttribute("aria-label"));
  if (direct) return direct.slice(0, MAX_LABEL);

  const labelledBy = el.getAttribute("aria-labelledby");
  if (labelledBy) {
    const named = labelledBy
      .split(/\s+/)
      .map((id) => clean(document.getElementById(id)?.textContent))
      .filter(Boolean)
      .join(" ");
    if (named) return named.slice(0, MAX_LABEL);
  }

  const attr = clean(el.getAttribute("title")) || clean(el.getAttribute("alt")) || clean(el.getAttribute("placeholder"));
  if (attr) return attr.slice(0, MAX_LABEL);

  // Own text counts as a name only for elements that ARIA calls "name from
  // content" — a button is named by its label, a `div` is not named by
  // everything that happens to sit inside it (fix #3).
  if (namedByContent(el)) {
    const own = clean(el.textContent);
    if (own && own.length <= MAX_LABEL) return own;
  }

  return null;
}

/** Elements whose accessible name comes from their own text content. */
const NAME_FROM_CONTENT_TAGS = new Set([
  "button", "a", "summary", "th", "td", "li", "option", "label", "legend",
  "h1", "h2", "h3", "h4", "h5", "h6",
]);

function namedByContent(el: Element): boolean {
  if (NAME_FROM_CONTENT_TAGS.has(el.tagName.toLowerCase())) return true;
  const role = el.getAttribute("role");
  return !!role && MEANINGFUL_ROLES.has(role);
}

/**
 * True when all we have is an unnamed layout container. Recorded on the row
 * so a reader can tell "he pointed at this button" from "he pointed
 * somewhere on this page and we could not narrow it down".
 */
function isAnonymousContainer(el: Element, label: string | null, testId: string | null): boolean {
  if (testId || label) return false;
  if (isMeaningful(el)) return false;
  return true;
}

export function describeElement(
  el: Element,
  pointer?: { x: number; y: number } | null,
): PickedElement {
  const rect = el.getBoundingClientRect();
  const raw = clean(el.textContent);
  const label = accessibleName(el);
  const testId = findTestId(el);

  return {
    selector: buildSelector(el),
    text: raw ? raw.slice(0, MAX_TEXT) : null,
    role: el.getAttribute("role") || el.tagName.toLowerCase(),
    label,
    testId,
    rect: {
      x: Math.round(rect.left + window.scrollX),
      y: Math.round(rect.top + window.scrollY),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
    },
    pointer: pointer ? { x: Math.round(pointer.x), y: Math.round(pointer.y) } : null,
    confident: !isAnonymousContainer(el, label, testId),
  };
}

/** Short human label for the composer chip, e.g. `button · "[add-account]"`. */
export function summarizePick(pick: PickedElement): string {
  if (!pick.confident) return "couldn't name this — your note still files";
  const name = pick.testId ? `[${pick.testId}]` : pick.label || pick.text;
  const label = name ? `"${name.slice(0, 60)}${name.length > 60 ? "…" : ""}"` : pick.selector;
  return `${pick.role} · ${label}`;
}
